import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Code2,
  Edit2,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { cn } from '@/lib/utils'
import type {
  TemplateFormData,
  TestTemplate,
  ValidationResult,
} from './hooks/use-test-templates'
import { useTestTemplates } from './hooks/use-test-templates'
import { TemplateEditorModal } from './template-editor-modal'

export function TestTemplatesSection() {
  const {
    templates,
    loading,
    error,
    refresh,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    validateTemplate,
  } = useTestTemplates()

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<TestTemplate | null>(
    null,
  )
  const [templateToDelete, setTemplateToDelete] = useState<TestTemplate | null>(
    null,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Filter templates by search query
  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      template.name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query) ||
      template.slug.toLowerCase().includes(query)
    )
  })

  const handleCreateOrUpdate = async (
    data: TemplateFormData,
  ): Promise<TestTemplate> => {
    setIsSubmitting(true)
    try {
      let result: TestTemplate
      if (editingTemplate) {
        result = await updateTemplate(editingTemplate.id, data)
        toast.success('Template updated')
      } else {
        result = await createTemplate(data)
        toast.success('Template created')
      }
      setShowModal(false)
      setEditingTemplate(null)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save'
      toast.error(message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleValidate = async (
    id: string,
    language: string,
    options?: {
      code?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ): Promise<ValidationResult> => {
    return validateTemplate(id, language, options)
  }

  const handleOpenModal = () => {
    setEditingTemplate(null)
    setShowModal(true)
  }

  const handleEdit = (template: TestTemplate) => {
    setEditingTemplate(template)
    setShowModal(true)
  }

  const handleDelete = (template: TestTemplate) => {
    setTemplateToDelete(template)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return
    setIsSubmitting(true)
    try {
      await deleteTemplate(templateToDelete.id)
      toast.success('Template deleted')
      setShowDeleteConfirm(false)
      setTemplateToDelete(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="mt-8 rounded-lg border border-border bg-card">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex w-full items-center justify-between p-4 transition-colors hover:bg-muted/50"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 p-2">
              <Code2 size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-foreground">
                Test Templates
              </h2>
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        {!isCollapsed && (
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <RefreshCw size={16} className="animate-spin" />
              Loading templates...
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mt-8 rounded-lg border border-border bg-card">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="-m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50"
        >
          <div className="rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 p-2">
            <Code2 size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-foreground">
              Test Templates
            </h2>
            <p className="text-sm text-muted-foreground">
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </p>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleOpenModal}>
            <Plus size={16} className="mr-1" />
            Add Template
          </Button>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Content - Collapsible */}
      {!isCollapsed && (
        <div className="border-t border-border p-4">
          {error ? (
            <div className="py-8 text-center">
              <p className="mb-2 text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                Retry
              </Button>
            </div>
          ) : templates.length === 0 ? (
            <div className="py-8 text-center">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Code2 size={24} className="text-muted-foreground" />
              </div>
              <h3 className="mb-2 font-medium text-foreground">
                No templates yet
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Create reusable code templates for test harness (e.g., TreeNode,
                ListNode).
              </p>
              <Button size="sm" onClick={handleOpenModal}>
                <Plus size={16} className="mr-1" />
                Create Template
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Search */}
              <div className="mb-4">
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="w-full"
                />
              </div>

              {/* Template List */}
              <div className="space-y-2">
                {filteredTemplates.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">No templates found</p>
                  </div>
                ) : (
                  filteredTemplates.map((template) => (
                    <TemplateListItem
                      key={template.id}
                      template={template}
                      onEdit={() => handleEdit(template)}
                      onDelete={() => handleDelete(template)}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <TemplateEditorModal
        isOpen={showModal}
        template={editingTemplate}
        onClose={() => {
          setShowModal(false)
          setEditingTemplate(null)
        }}
        onSave={handleCreateOrUpdate}
        onValidate={handleValidate}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && templateToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Delete Template
                </h2>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="mb-6 text-foreground">
              Are you sure you want to delete{' '}
              <strong>{templateToDelete.name}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setTemplateToDelete(null)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Separate component for template list item
interface TemplateListItemProps {
  template: TestTemplate
  onEdit: () => void
  onDelete: () => void
}

function TemplateListItem({
  template,
  onEdit,
  onDelete,
}: TemplateListItemProps) {
  const languageSummary = template.languageSummary || {}

  return (
    <div className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground">{template.name}</h3>
          {/* Language validation indicators */}
          <div className="flex items-center gap-1">
            {languageSummary.python !== undefined && (
              <span
                className="flex items-center gap-0.5 text-xs"
                title={
                  languageSummary.python
                    ? 'Python validated'
                    : 'Python not validated'
                }
              >
                {languageSummary.python ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className="text-muted-foreground">py</span>
              </span>
            )}
            {languageSummary.javascript !== undefined && (
              <span
                className="flex items-center gap-0.5 text-xs"
                title={
                  languageSummary.javascript
                    ? 'JavaScript validated'
                    : 'JavaScript not validated'
                }
              >
                {languageSummary.javascript ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className="text-muted-foreground">js</span>
              </span>
            )}
          </div>
        </div>
        {template.description && (
          <p className="line-clamp-1 text-sm text-muted-foreground">
            {template.description}
          </p>
        )}
        <div className="mt-1 text-xs text-muted-foreground">
          <code>{template.slug}</code>
        </div>
      </div>
      <div className={cn(REVEAL_ON_HOVER, 'flex items-center gap-1')}>
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-2 text-muted-foreground transition-all hover:text-foreground"
          title="Edit template"
        >
          <Edit2 size={16} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-2 text-muted-foreground transition-all hover:text-destructive"
          title="Delete template"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  )
}
